import React, { useState, useEffect, useCallback, useRef } from "react"
import _get from "lodash/get"
import _debounce from "lodash/debounce"
import { useDispatch, useSelector } from "react-redux"
import useSearchi18n from "@/i18n/useSearchi18n"
import { aemAxiosInstance } from "@/constants/api";
import { LEAD_TIME_DOCUMENT_ENDPOINT, PRODUCTS_PER_PAGE } from "@/constants/index"
import { getConfig } from "@/constants/config"
import {
  addToRecentSearch,
  isShowAnalyticsEventLoaded,
  getShortenedUrl,
  getParamFromUrl,
  removeSearchQueryParams,
  getBadgeValue,
} from "@/utils/helper"
import {
  fetchSearchAllResults,
  fetchSearchServicePartsResults,
  searchState,
} from "@/store/features/searchSlice"

import SearchViewComponent from "@/components/Search/v1/SearchView"
import InspirationTab from "@/components/Search/v1/InspirationTab"
import TechnicalTab from "@/components/Search/v1/TechnicalTab/index"
import ProductsTab from "@/components/Search/v1/ProductsTab"
import OuterSearchProgressive from "@/components/Search/v1/OuterSearchProgressive"

const SearchComponent = props => {
  const dispatch = useDispatch()
  const newState = useSelector(searchState)
  const { searchResponse } = newState
  const productsPerPage = PRODUCTS_PER_PAGE
  const { data: componentProp, header: headerProps } = props
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState({})
  const [partsData, setPartsData] = useState({})
  const [searchKeyword, setSearchKeyword] = useState("")
  const [searchMainKeyword, setSearchMainKeyword] = useState("")
  const [loading, setLoading] = useState(true)
  const [exactMatch, setExactMatch] = useState(false)
  const [tagDebug, setTagDebug] = useState(true)
  const [searchClickEvent, setSearchClickEvent] = useState(false)
  const [debounceInProgress, setDebounceInProgress] = useState(false)
  const [isFromServicePart, setIsFromServicePart] = useState(false)
  const [general, setGeneral] = useState()
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  // eslint-disable-next-line no-unused-vars
  const [info, setInfo] = useState({})
  const [loadPopularSearch, setLoadPopularSearch] = useState(false)
  const [isSterling, setIsSterling] = useState(false)
  const [plpBadge, setPlpBadge] = useState({})
  const [leadTimeData, setLeadTimeData] = useState(null)

  const pageLoadedRef = useRef(false)

  const staticTexts = useSearchi18n({
    componentProp,
  })

  useEffect(() => {
    getConfig().then(async config => {
      const { plpConfigurations: { myArrayList: plpBadgeList = [] } = {} } =
        config
      const brandName = config?.general?.siteName ?? ""
      const locale = _get(config, "internationalization.locale", "")
      const currencySign = _get(config, "internationalization.currencySign", "")
      const currencyCode = _get(config, "internationalization.currencyCode", "")
      const knowledgeArticleUrl = await getShortenedUrl(
        _get(config, "general.knowledgeArticlePath", "")
      )
      const marketParamValues = _get(config, "marketParamValues")

      const { general: { lwAppName = "" } = {} } = config ?? {}

      const info = {
        locale: locale,
        currencySign: currencySign,
        currencyCode: currencyCode,
        knowledgeArticleUrl: knowledgeArticleUrl,
        lwAppName,
      }

      const displayOrderBadges =
        plpBadgeList.find(item => item.map.Key === "displayOrderBadges") ?? {}
      const {
        map: { Values: { myArrayList: displayOrderBadgeList = [] } = {} } = {},
      } = displayOrderBadges

      const plpBadges = {}
      displayOrderBadgeList.forEach(item => {
        plpBadges[item] = getBadgeValue(item, plpBadgeList)
      })
      const general = {
        brandName: brandName,
        marketParamValues: marketParamValues,
        lwAppName: lwAppName,
      }
      setInfo(info)
      setGeneral(general)
      setPlpBadge({
        plpBadges,
        displayOrderBadgeList,
      })
      setTimeout(function () {
        setIsInitialLoad(false)
      }, 2000)
    })
    try {
      const urlParams = new URL(window?.location?.href)
      if (urlParams?.searchParams?.get("keyword")) {
        const keyword = decodeURIComponent(
          urlParams?.searchParams?.get("keyword")
        )
        const isFromServicePart = decodeURIComponent(
          urlParams?.searchParams.get("service")
        )

        if (keyword?.length < 3) {
          setSearchKeyword(keyword)
          setLoadPopularSearch(true)
          setLoading(false)
        } else {
          setSearchKeyword(keyword)
          setIsFromServicePart(isFromServicePart === "true")
          setLoading(true)
        }
      } else {
        setLoadPopularSearch(true)
        setLoading(false)
      }
    } catch (error) {
      setLoadPopularSearch(true)
      setLoading(false)
      console.error("URIError: malformed URI sequence")
    }

    aemAxiosInstance({
      url: LEAD_TIME_DOCUMENT_ENDPOINT,
      params: {
        path:"/content/annsacks/us/en/lead-time",
        contextPath:"/content/annsacks"
      }})
      .then(res => {
        if (res?.data) {
          let leadTimeData = {
            contentSource_s: "asset",
            eyeBrowText_s: res?.data[0]?.pageTitle,
            externalRedirectUrl_s: res.data[0]?.leadTimeUrl,
            id: res.data[0]?.id,
            leadTime_b: true,
            pageTitle_s: res?.data[0]?.["jcr:description"],
            title_s: res?.data[0]?.["page-description-content"],
          }
          setLeadTimeData(leadTimeData)
        }
        })
      .catch(err => {
        setLeadTimeData(null)
        console.error("Failed to get compare attributes JSON file", err)
      })
  }, [])

  useEffect(() => {
    const fullcatalogResponse =
      searchResponse?.data?.fusion?.full_catalog_Response
    if (fullcatalogResponse?.length) {
      if (fullcatalogResponse[0]["Brand_ss"][0] === "Sterling") {
        setIsSterling(true)
      }
    } else {
      setIsSterling(false)
    }
  }, [searchResponse])

  useEffect(() => {
    if (general) {
      if (searchKeyword?.length > 2 && isInitialLoad) {
        getResults(searchKeyword, general?.brandName?.toLowerCase())?.catch(
          () => {}
        )
        setSearchMainKeyword(searchKeyword)
        removeSearchQueryParams()
        setIsInitialLoad(false)
      } else {
        debounceAndFetch(searchKeyword)
      }
    }
  }, [searchKeyword, tagDebug, general])

  const addSearchAnalytics = params => {
    const {
      total,
      errorMessage,
      status,
      autoSearch,
      keyword = "",
      tabCountObj = {},
      isSterlingTemp,
      searchCountIncreased = true,
    } = params
    const action = autoSearch ? "cmp:click" : params?.action
    const {
      productTabCount,
      helpTabCount,
      resourceTabCount,
      technicalTabCount,
      inspireTabCount,
      partTabCount,
    } = tabCountObj

    const currDate = new Date()
    const eventInfo = {
      searchCount: sessionStorage?.getItem("searches")
        ? Number(sessionStorage?.getItem("searches")) +
          (searchCountIncreased ? 1 : 0)
        : 1,
      searchResult: isSterlingTemp ? `${productTabCount}` : total,
      searchResultCategory: isSterlingTemp
        ? `products>${productTabCount}`
        : `products>${productTabCount}|parts>${partTabCount}|inspiration>${inspireTabCount}|technical>${technicalTabCount}|resources>${resourceTabCount}|help>${helpTabCount}`,
      internalLinkName: "search",
      internalLinkPosition: "search results:search",
      internalLinkType: autoSearch
        ? "search:automatic search"
        : "search results:search:navigation",
      searchTerm: keyword.length ? keyword : searchKeyword,
      searchSuggestedTerm: "n/a",
      eventAction: `search:search box:${autoSearch ? "automatic " : ""}search`,
      eventName: `search:search box:${autoSearch ? "automatic " : ""}search`,
      eventType: "navigation",
      clickInternalLinks: "true",
      internalLinkURL: action === "cmp:show" ? "n/a" : window?.location?.href,
      internalZoneName: "search:search box",
      eventMsg: errorMessage || "n/a",
      eventStatus: status === "success" ? "success" : "failure",
      pageName: "search result",
      pageURL: window?.location?.href,
      referrer: document?.referrer || "n/a",
      previousPage: document?.referrer || "n/a", // we can not get previous page name
      timeStamp:
        currDate?.toISOString()?.split("T")[0] +
        " " +
        currDate?.toTimeString()?.split(" ")[0],
      mktgCampaign: "n/a", // currently we don't have such implementation
      contentType: general?.brandName?.toLowerCase(),
      internalLinkZoneName: "search:search box",
    }
    sessionStorage?.setItem("searches", eventInfo?.searchCount)

    const { adobeDataLayer: { getState } = {} } = window
    const page =
      action === "cmp:show"
        ? window?.pageObj
        : (getState && getState("page")) || {}
    const dtLayerObj = {
      event: action,
      page,
    }

    if (action === "cmp:show" || action === "cmp:click") {
      dtLayerObj.eventInfo = eventInfo
    }

    window?.adobeDataLayer?.push({ ...dtLayerObj })
  }
  const debounceAndFetch = useCallback(
    _debounce(key => {
      if (key?.length > 2) {
        getResults(key, general?.brandName?.toLowerCase())?.catch(() => {})
        setSearchMainKeyword(key)
        removeSearchQueryParams()
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    }, 2000),
    [general?.brandName?.toLowerCase(), tagDebug]
  )
  const handleSearch = (keyword, clear, tagger) => {
    setSearchKeyword(keyword)
    setTagDebug(!tagger)
    setDebounceInProgress(true)

    if (keyword?.length === 0) {
      setTotal(0)
      setLoadPopularSearch(true)
    }
  }
  const handleSearchEnter = () => {
    if (debounceInProgress || loading) {
      setSearchClickEvent(true)
    } else {
      // this means no any requests are in progress. just add click event directly
      addSearchAnalytics({
        action: "cmp:click",
        status: "success",
        total: total,
        autoSearch: false,
      })
    }
  }
  const setUrlParam = keyword => {
    if (keyword) {
      const url = new URL(window?.location?.href)
      const searchParams = url?.searchParams
      let urlSearch = "?" + searchParams?.toString()
      if (searchParams.get("keyword") !== keyword) {
        urlSearch = `?keyword=${keyword}`
      }
      window?.history?.replaceState(
        window?.location?.pathname,
        document?.title,
        urlSearch
      )
    }
  }
  const getResults = (keyword, brandNameVal = "kohler") => {
    const { disableParts } = staticTexts
    return new Promise((resolve, reject) => {
      setLoading(true)
      setDebounceInProgress(false)
      setLoadPopularSearch(false)
      let params = {
        q: keyword,
        rows: 1,
        "lw.tagger.debug": tagDebug,
        collections: brandNameVal,
        displaytype: "search",
      }
      if (isFromServicePart) {
        params = {
          q: keyword,
          rows: 1,
          "lw.tagger.debug": tagDebug,
          collections: brandNameVal,
          service: true,
          displaytype: "search",
        }
      }

      Promise.all([
        dispatch(fetchSearchAllResults({ ...params })),

        dispatch(
          fetchSearchServicePartsResults({
            q: keyword,
            collections: brandNameVal,
            displaytype: "search",
          })
        ),
      ])
        .then(resp => {
          const firstResponse = resp[0]
          const firstRespPayload = firstResponse?.payload
          const firstRespFusion = firstRespPayload?.fusion
          const fullCatalogResponse = firstRespFusion?.full_catalog_Response
          let isSterlingTemp = false
          if (fullCatalogResponse?.length) {
            if (fullCatalogResponse[0]["Brand_ss"][0] === "Sterling") {
              isSterlingTemp = true
              setIsSterling(true)
            }
          }
          const isShowEvent = isShowAnalyticsEventLoaded()
          const isClickEvent = searchClickEvent
          if (
            resp[0]?.payload?.fusion?.redirect &&
            resp[0]?.payload?.fusion?.redirect[0]
          ) {
            window.location.href = resp[0]?.payload?.fusion?.redirect[0]
            addSearchAnalytics({
              action: isClickEvent ? "cmp:click" : "cmp:show",
              status: "success",
              total: 1,
              autoSearch: !isClickEvent && isShowEvent,
            })
            resolve()
          } else if (resp[0]?.payload && resp[1]?.payload) {
            addToRecentSearch(keyword)
            let groups = [
              {
                key: "products",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Products[1]",
                  0
                ),
              },
              {
                key: "parts",
                count: _get(resp[1]?.payload, "response.numFound", 0),
              },
              {
                key: "technical",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Technical[1]",
                  0
                ),
              },
              {
                key: "inspiration",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Inspiration[1]",
                  0
                ),
              },
              {
                key: "resource",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Resource[1]",
                  0
                ),
              },
              {
                key: "help",
                count: _get(
                  resp[0]?.payload,
                  "facet_counts.facet_fields.Help[1]",
                  0
                ),
              },
            ]
            if (disableParts || !isSterling) {
              groups = groups?.filter(arr => {
                return arr?.key !== "parts"
              })
            }
            // Calculating the total results
            let total = 0
            const selected = groups?.find(group => group?.count > 0)
            if (
              resp[0]?.payload?.fusion?.exactMatch ||
              resp[1]?.payload?.fusion?.exactMatch
            ) {
              total =
                selected?.key === "parts"
                  ? resp[1]?.payload?.response?.numFound
                  : resp[0]?.payload?.response?.numFound
            } else {
              groups?.map(group => (total += group?.count))
            }
            if (total === 0) {
              setLoadPopularSearch(true)
            }
            setLoading(false)
            // setData(resp[0].payload)
            setPartsData(resp[1]?.payload)
            if (total > 0) setTotal(1 + total)
            else setTotal(total)
            setSelected(selected)
            setExactMatch(
              selected?.key === "parts"
                ? resp[1]?.payload?.fusion?.exactMatch
                : resp[0]?.payload?.fusion?.exactMatch
            )
            setSearchClickEvent(false)
            setUrlParam(keyword)
            const productTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Products[1]",
              0
            )
            const inspireTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Inspiration[1]",
              0
            )

            const technicalTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Technical[1]",
              0
            )
            const resourceTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Resource[1]",
              0
            )
            const helpTabCount = _get(
              resp[0].payload,
              "facet_counts.facet_fields.Help[1]",
              0
            )
            const partTabCount = _get(resp[1].payload, "response.numFound", 0)
            const tabCountObj = {
              productTabCount: isSterlingTemp ? 1 : productTabCount,
              helpTabCount,
              resourceTabCount,
              technicalTabCount,
              inspireTabCount,
              partTabCount,
            }
            addSearchAnalytics({
              action: isClickEvent ? "cmp:click" : "cmp:show",
              status: "success",
              total,
              autoSearch: !isClickEvent && isShowEvent,
              keyword: keyword,
              tabCountObj,
              isSterlingTemp,
            })

            // if the url changes on subsequent searches then log a cmp:show as well
            if (pageLoadedRef.current) {
              addSearchAnalytics({
                action: "cmp:show",
                status: "success",
                total,
                autoSearch: false,
                keyword: keyword,
                tabCountObj,
                isSterlingTemp,
                searchCountIncreased: false,
              })
            } else {
              pageLoadedRef.current = true
            }
            resolve()
          }
        })
        ?.catch(err => {
          // eslint-disable-next-line no-console
          console.log("Error occured while fetching Search data", err)
          const isShowEvent = isShowAnalyticsEventLoaded()
          const isClickEvent = searchClickEvent
          setLoading(false)
          setSearchClickEvent(false)
          setLoadPopularSearch(true)
          addSearchAnalytics({
            action: isClickEvent ? "cmp:click" : "cmp:show",
            errorMessage: staticTexts?.noResults?.toLowerCase(),
            status: "failure",
            total: 0,
            autoSearch: !isClickEvent && isShowEvent,
          })
          reject(new Error("something bad happened"))
        })
    })
  }
  const staticData = staticTexts
  const { disableParts } = staticData

  const tabList = []
  if (
    _get(searchResponse?.data, "facet_counts.facet_fields.Products", [])
      ?.length > 1
  ) {
    const count = isSterling
      ? 1
      : _get(searchResponse?.data, "facet_counts.facet_fields.Products[1]", 0)
    tabList?.push({
      id: "Products",
      title: staticTexts?.products,
      count: count,
      content: (
        <ProductsTab
          staticTexts={staticTexts}
          page="search"
          key="Products"
          type="Products"
          searchKeyword={searchMainKeyword}
          totalSearchResults={isSterling ? 1 : total}
          count={count}
          currencySign={info?.currencySign}
          onSearch={handleSearch}
          query={'Products:("ct")'}
          lwAppName={general?.lwAppName}
          componentProps={componentProp}
          presetConfigs={props?.data?.presetConfigs}
          marketParamValues={general?.marketParamValues}
          isSterling={isSterling}
          sterlingData={
            isSterling && searchResponse.data.fusion.full_catalog_Response
          }
          showSterlingButton={true}
          plpBadge={plpBadge}
        />
      ),
    })
  }
  if (!isSterling) {
    if (
      _get(partsData, "response.numFound", 0) > 0 &&
      !disableParts &&
      general?.lwAppName.toLowerCase() !== "annsacks"
    ) {
      const count = _get(partsData, "response.numFound", 0)
      tabList?.push({
        id: "Parts",
        title: staticTexts?.parts,
        count: count,
        content: (
          <ProductsTab
            staticTexts={staticTexts}
            page="search"
            key="Parts"
            type="Parts"
            count={count}
            searchKeyword={searchMainKeyword}
            totalSearchResults={total}
            currencySign={info?.currencySign}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            componentProps={componentProp}
            presetConfigs={props?.data?.presetConfigs}
            marketParamValues={general?.marketParamValues}
            isSterling={isSterling}
            sterlingData={
              isSterling && searchResponse.data.fusion.full_catalog_Response
            }
            lwAppName={general?.lwAppName}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Inspiration", [])
        ?.length > 1
    ) {
      const count = _get(
        searchResponse?.data,
        "facet_counts.facet_fields.Inspiration[1]",
        0
      )
      tabList?.push({
        id: "Inspiration",
        title: staticTexts.inspiration,
        count: count,
        content: (
          <InspirationTab
            page="search"
            key="Inspiration"
            type="Inspiration"
            searchKeyword={searchMainKeyword}
            totalResults={count}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            fquery={'Inspiration:("Inspiration")'}
            staticTexts={staticTexts}
            knowledgeArticleUrl={info?.knowledgeArticleUrl}
            marketParamValues={general?.marketParamValues}
            lwAppName={general?.lwAppName}
            profileName={`profile_${general?.lwAppName}_Search`}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Technical", [])
        ?.length > 1
    ) {
      const count = _get(
        searchResponse?.data,
        "facet_counts.facet_fields.Technical[1]",
        0
      )
      tabList?.push({
        id: "Technical",
        title: staticTexts?.technical,
        count: count,
        content: (
          <TechnicalTab
            page="search"
            staticTexts={staticTexts}
            key="Technical"
            type="Technical"
            searchKeyword={searchMainKeyword}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            totalResults={total}
            marketParamValues={general?.marketParamValues}
          />
        ),
      })
    }
    if (_get(searchResponse?.data, "facet_counts.facet_fields.Resource", [])?.length >= 0 ) {
      const count = 1 +_get(searchResponse?.data, "facet_counts.facet_fields.Resource[1]", 0)
      tabList?.push({
        id: "Resources",
        title: staticTexts?.resource,
        count: count,
        content: (
          <InspirationTab
            page="search"
            key="Resources"
            type="Resources"
            searchKeyword={searchMainKeyword}
            totalResults={total}
            leadTimeData={leadTimeData}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            fquery={'Resource:("Resource")'}
            staticTexts={staticTexts}
            knowledgeArticleUrl={info.knowledgeArticleUrl}
            lwAppName={general?.lwAppName}
            profileName={`profile_${general?.lwAppName}_Search`}
          />
        ),
      })
    }
    if (
      _get(searchResponse?.data, "facet_counts.facet_fields.Help", [])?.length >
      1
    ) {
      const count = _get(
        searchResponse?.data,
        "facet_counts.facet_fields.Help[1]",
        0
      )
      tabList?.push({
        id: "Help",
        title: staticTexts?.help,
        count: count,
        content: (
          <InspirationTab
            page="search"
            staticTexts={staticTexts}
            key="Help"
            type="Help"
            searchKeyword={searchMainKeyword}
            totalResults={count}
            onSearch={handleSearch}
            productsPerPage={productsPerPage}
            fquery={'Help:("sf")'}
            knowledgeArticleUrl={info.knowledgeArticleUrl}
            totalSearchResults={total}
            displayShare={true}
            displayPrint={true}
          />
        ),
      })
    }
  }

  let activeTab
  const currentTab = getParamFromUrl("tab") || ""
  if (currentTab) activeTab = tabList?.findIndex(tab => tab?.id === currentTab)
  else {
    if (_get(searchResponse?.data, "fusion.default_tab[0]", "")) {
      activeTab = tabList?.findIndex(
        tab => tab?.id === searchResponse?.data?.fusion?.default_tab[0]
      )
    }
  }

  if (exactMatch)
    tabList?.forEach((tab, index) => {
      if (tab?.id !== selected?.key) {
        tabList?.splice(1, index)
      }
    })

  if (searchResponse?.data || searchKeyword?.length < 3) {
    return (
      <>
        <SearchViewComponent
          activeTabVal={activeTab}
          loading={loading}
          data={componentProp}
          staticTexts={staticTexts}
          totalResults={isSterling ? 1 : total}
          searchKeyword={searchKeyword}
          tabList={tabList}
          selected={selected}
          exactMatch={exactMatch}
          onSearch={handleSearch}
          brand={general?.brandName}
          handleSearchEnter={handleSearchEnter}
          staticData={staticData}
          loadPopularSearch={loadPopularSearch}
          searchResponse={searchResponse}
        />
      </>
    )
  } else {
    return <OuterSearchProgressive />
  }
}

export default SearchComponent
